import React, { useState, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import { ArrowBack, Search, ExpandMore, ThumbUp, ThumbDown, Gavel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFaqList, useSubmitFaqFeedback } from '../../hooks/useFaq';
import { useMySanctions } from '../../hooks/useSanction';
import { formatDateKST } from '../../utils/dateUtils';
import type { FaqCategory } from '../../types/report';

const CATEGORY_LABELS: Record<FaqCategory | 'ALL', string> = {
  ALL: '전체',
  ACCOUNT_PROFILE: '계정/프로필',
  PAYMENT_REFUND: '결제/환불',
  MATCHING: '매칭/큐피드카드',
  CHAT: '채팅',
  PRIVACY: '개인정보',
  REPORT_BLOCK: '신고/차단',
  USAGE_RESTRICTION: '이용 제한',
};

const CATEGORIES = Object.keys(CATEGORY_LABELS) as Array<FaqCategory | 'ALL'>;

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<FaqCategory | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<number | false>(false);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<number, boolean>>({});

  const { faqs, loading, error } = useFaqList(
    activeCategory === 'ALL' ? undefined : activeCategory
  );
  const { submit: submitFeedback } = useSubmitFaqFeedback();
  const { sanctions, hasSanctions } = useMySanctions();

  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return faqs;
    const q = searchQuery.toLowerCase();
    return faqs.filter(
      (f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    );
  }, [faqs, searchQuery]);

  const top5Faqs = useMemo(
    () => [...faqs].sort((a, b) => b.viewCount - a.viewCount).slice(0, 5),
    [faqs]
  );

  const handleFeedback = async (id: number, helpful: boolean) => {
    if (feedbackGiven[id] !== undefined) return;
    setFeedbackGiven((prev) => ({ ...prev, [id]: helpful }));
    await submitFeedback(id, helpful);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            고객센터
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* TOP 5 */}
        {!searchQuery && activeCategory === 'ALL' && top5Faqs.length > 0 && (
          <Paper
            elevation={0}
            sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1rem' }}>
              자주 묻는 질문 TOP 5
            </Typography>
            {top5Faqs.map((faq, idx) => (
              <Box
                key={faq.id}
                onClick={() => {
                  setExpandedId(faq.id === expandedId ? false : faq.id);
                  setTimeout(() => {
                    document.getElementById(`faq-${faq.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 100);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <Typography sx={{ fontWeight: 700, color: 'primary.main', minWidth: 20 }}>
                  {idx + 1}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem' }}>{faq.question}</Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* 검색바 */}
        <TextField
          fullWidth
          placeholder="질문을 검색하세요"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
          inputProps={{ style: { fontSize: '1rem' } }}
        />

        {/* 카테고리 탭 */}
        <Tabs
          value={activeCategory}
          onChange={(_, v) => { setActiveCategory(v); setSearchQuery(''); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {CATEGORIES.map((cat) => (
            <Tab
              key={cat}
              value={cat}
              label={CATEGORY_LABELS[cat]}
              sx={{ fontSize: '0.9rem', minHeight: 48, fontWeight: 600 }}
            />
          ))}
        </Tabs>

        {/* FAQ 목록 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && filteredFaqs.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4, fontSize: '1rem' }}>
            {searchQuery ? '검색 결과가 없습니다.' : '등록된 FAQ가 없습니다.'}
          </Typography>
        )}

        {filteredFaqs.map((faq) => (
          <Accordion
            key={faq.id}
            id={`faq-${faq.id}`}
            expanded={expandedId === faq.id}
            onChange={(_, isExpanded) => setExpandedId(isExpanded ? faq.id : false)}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { my: 1.5 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                {faq.isPinned && (
                  <Chip label="공지" size="small" color="primary" sx={{ fontSize: '0.75rem', height: 22 }} />
                )}
                <Typography sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 }}>
                  {faq.question}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.8, whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                {faq.answer}
              </Typography>

              {/* 피드백 */}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mr: 1 }}>
                  도움이 되었나요?
                </Typography>
                {feedbackGiven[faq.id] !== undefined ? (
                  <Typography sx={{ fontSize: '0.85rem', color: 'primary.main' }}>
                    피드백 감사합니다 🙏
                  </Typography>
                ) : (
                  <>
                    <IconButton size="small" onClick={() => handleFeedback(faq.id, true)} aria-label="도움됨">
                      <ThumbUp fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleFeedback(faq.id, false)} aria-label="도움안됨">
                      <ThumbDown fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* 이의제기 섹션 (제재가 있는 사용자에게만 표시) */}
        {hasSanctions && (
          <Paper
            elevation={0}
            sx={{ mt: 3, p: 3, borderRadius: 2, border: '1px solid', borderColor: 'error.light', bgcolor: 'error.50' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Gavel sx={{ color: 'error.main' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'error.main' }}>
                이용 제한 안내
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', mb: 2 }}>
              회원님에게 적용된 제재가 있습니다. 이의가 있으시면 이의제기를 통해 재검토를 요청하실 수 있습니다.
            </Typography>
            {sanctions.map((s) => (
              <Box
                key={s.reportId}
                sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  p: 2, mb: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={s.sanctionTypeTitle}
                      size="small"
                      color={s.sanctionType === 'BAN' ? 'error' : s.sanctionType === 'SUSPENSION' ? 'warning' : 'default'}
                      sx={{ fontSize: '0.8rem', height: 24 }}
                    />
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {s.categoryTitle}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {formatDateKST(s.createdAt)}
                  </Typography>
                </Box>
                {s.hasAppealed ? (
                  <Chip label="이의제기 완료" size="small" variant="outlined" sx={{ fontSize: '0.8rem' }} />
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => navigate(`/appeal?sanctionId=${s.reportId}&sanctionType=${s.sanctionType}`)}
                    sx={{ fontSize: '0.85rem', fontWeight: 600 }}
                  >
                    이의제기
                  </Button>
                )}
              </Box>
            ))}
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate('/appeal/list')}
              sx={{ mt: 1, fontSize: '0.9rem', color: 'text.secondary' }}
            >
              이의제기 내역 보기
            </Button>
          </Paper>
        )}

        {/* 하단 문의 유도 */}
        <Paper
          elevation={0}
          sx={{ mt: 4, p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '1rem', mb: 2, color: 'text.secondary' }}>
            원하는 답변을 찾지 못하셨나요?
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate('/support/inquiry')}
            sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700, mb: 1.5 }}
          >
            1:1 문의하기
          </Button>
          <Button
            variant="text"
            onClick={() => navigate('/support/inquiry/list')}
            sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
          >
            내 문의 내역 보기
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default SupportPage;
